import React, { useEffect, useState } from 'react';
import { liabilitiesAPI } from '../services/api';

interface Liability {
  id: number;
  category: string;
  name: string;
  balance: number;
  interest_rate?: number;
  minimum_payment?: number;
  notes?: string;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
  }).format(value);
};

const Liabilities: React.FC = () => {
  const [liabilities, setLiabilities] = useState<Liability[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLiability, setEditingLiability] = useState<Liability | null>(null);
  const [formData, setFormData] = useState({
    category: '',
    name: '',
    balance: '',
    interest_rate: '',
    minimum_payment: '',
    notes: '',
  });

  const categories = ['Mortgage', 'Auto Loan', 'Credit Card', 'Student Loan', 'Line of Credit', 'Other'];

  useEffect(() => {
    fetchLiabilities();
  }, []);

  const fetchLiabilities = async () => {
    try {
      const response = await liabilitiesAPI.getAll();
      setLiabilities(response.data);
    } catch (error) {
      console.error('Failed to fetch liabilities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        category: formData.category,
        name: formData.name,
        balance: parseFloat(formData.balance),
        interest_rate: formData.interest_rate ? parseFloat(formData.interest_rate) : null,
        minimum_payment: formData.minimum_payment ? parseFloat(formData.minimum_payment) : null,
        notes: formData.notes,
      };

      if (editingLiability) {
        await liabilitiesAPI.update(editingLiability.id, data);
      } else {
        await liabilitiesAPI.create(data);
      }

      setShowModal(false);
      setEditingLiability(null);
      setFormData({ category: '', name: '', balance: '', interest_rate: '', minimum_payment: '', notes: '' });
      fetchLiabilities();
    } catch (error) {
      console.error('Failed to save liability:', error);
    }
  };

  const handleEdit = (liability: Liability) => {
    setEditingLiability(liability);
    setFormData({
      category: liability.category,
      name: liability.name,
      balance: liability.balance.toString(),
      interest_rate: liability.interest_rate?.toString() || '',
      minimum_payment: liability.minimum_payment?.toString() || '',
      notes: liability.notes || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this liability?')) {
      try {
        await liabilitiesAPI.delete(id);
        fetchLiabilities();
      } catch (error) {
        console.error('Failed to delete liability:', error);
      }
    }
  };

  const totalLiabilities = liabilities.reduce((sum, l) => sum + l.balance, 0);

  const groupedLiabilities = liabilities.reduce((acc, liability) => {
    if (!acc[liability.category]) {
      acc[liability.category] = [];
    }
    acc[liability.category].push(liability);
    return acc;
  }, {} as Record<string, Liability[]>);

  if (loading) {
    return <div>Loading liabilities...</div>;
  }

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="page-title">Liabilities</h1>
          <p className="page-subtitle">Total: {formatCurrency(totalLiabilities)}</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          + Add Liability
        </button>
      </div>

      {Object.entries(groupedLiabilities).map(([category, categoryLiabilities]) => (
        <div key={category} className="card" style={{ marginBottom: '1rem' }}>
          <div className="card-header">
            <h3 className="card-title">{category}</h3>
            <span style={{ color: 'var(--danger)', fontWeight: 600 }}>
              {formatCurrency(categoryLiabilities.reduce((sum, l) => sum + l.balance, 0))}
            </span>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th style={{ textAlign: 'right' }}>Balance</th>
                  <th style={{ textAlign: 'right' }}>Interest Rate</th>
                  <th style={{ textAlign: 'right' }}>Min Payment</th>
                  <th style={{ width: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categoryLiabilities.map((liability) => (
                  <tr key={liability.id}>
                    <td>{liability.name}</td>
                    <td style={{ textAlign: 'right', fontWeight: 500, color: 'var(--danger)' }}>
                      {formatCurrency(liability.balance)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {liability.interest_rate ? `${liability.interest_rate}%` : '-'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {liability.minimum_payment ? formatCurrency(liability.minimum_payment) : '-'}
                    </td>
                    <td>
                      <div className="table-actions">
                        <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(liability)}>
                          Edit
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(liability.id)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {liabilities.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <p style={{ color: 'var(--text-secondary)' }}>No liabilities yet. Click "Add Liability" to get started.</p>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingLiability ? 'Edit Liability' : 'Add Liability'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Category</label>
                <select
                  className="form-input"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  <option value="">Select category</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., TD Mortgage"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Balance ($)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={formData.balance}
                  onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Interest Rate (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={formData.interest_rate}
                    onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
                    placeholder="5.25"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Min Payment ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={formData.minimum_payment}
                    onChange={(e) => setFormData({ ...formData, minimum_payment: e.target.value })}
                    placeholder="500.00"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes (optional)</label>
                <textarea
                  className="form-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional notes..."
                  rows={3}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingLiability ? 'Update' : 'Add'} Liability
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Liabilities;
